/**
 * Meeting Minutes — detail / editor.
 *
 * Routes
 *   /meeting-minutes/new           mints an empty draft server-side, then redirects to it,
 *                                  so there is never an "unsaved new record" state to lose.
 *   /meeting-minutes/:minutesId    the editor for that record.
 *
 * The page is a single editable document rather than a wizard: minutes are written live
 * during a meeting, and a step-by-step flow would be unusable at that speed. Everything is
 * held in a local draft and written back on Save, with two deliberate exceptions — the
 * status transitions and the carry-forward panel, both of which write immediately because
 * they change *other* records or lock this one.
 *
 * Status is a server-side state machine (draft → circulated → approved). It is not a
 * writable attribute, so it never appears in the save payload; it moves only through
 * meetingMinutesApi.transition. `approved` is terminal and freezes the whole record.
 *
 * Carry-forward is the reason this register exists. Every category raised in a previous
 * meeting on the same initiative can be carried into this one, but actions behave
 * differently from the other five: an open action is *consumed* by carrying it over
 * (closed where it was raised, copied here — never open in two places at once), while a
 * decision, risk, blocker, dependency or note is a live reference — logging it here copies
 * it without touching the original, and resolving it closes the original without copying
 * anything. See the carry-forward panel below for both flows.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';

import { NimbusModal, NimbusModalContent } from '../../components/NimbusModal';
import { PageHeader } from '../../components/PageHeader';
import { RichTextField } from '../../components/RichTextField';
import { RichTextView } from '../../components/RichTextView';
import { newRowId, useRepeatableRows } from '../../components/RepeatableRows';
import { MinuteEntryRow, entryPlainText, todayIso } from './MinuteEntryRow';
import type { SelectOption } from './MinuteEntryRow';

import { meetingMinutesApi, peopleApi } from '../../services/api';
import { initiativesApi } from '../../services/trackerApi';
import type { MeetingMinutes, MinuteCategory, MinuteEntry, MinutesStatus, Person } from '../../types/models';
import type { Initiative } from '../../types/trackerModels';
import {
  MINUTE_CATEGORIES,
  MINUTE_CATEGORY_ICON,
  MINUTE_CATEGORY_LABELS,
  MINUTE_CATEGORY_TONE,
  MINUTES_ACTION_LABELS,
  MINUTES_STATUS_LABELS,
  MINUTES_STATUS_TONE,
  MINUTES_STATUS_TRANSITIONS,
  categoryTakesOwner,
} from '../../constants/meetingMinutes';
import { reactSelectStyles } from '../../utils/reactSelectStyles';
import { formatDate } from '../../utils/format';
import { exportMeetingMinutesPdf } from '../../utils/meetingMinutesPdf';
import { useToast } from '../../hooks/useToast';
import { useSwal } from '../../hooks/useSwal';
import { useCurrentUser } from '../../hooks/useCurrentUser';

// ─── Draft model ──────────────────────────────────────────────────────────────
// useRepeatableRows keys rows by `id`; MinuteEntry keys by `entryId`. Rather than
// generalise the shared hook, the editor carries a mirror `id` on each row and strips
// it again on the way to the API — the two always hold the same uuid.

interface EntryRow extends MinuteEntry {
  id: string;
}

type MinutesDraft = Omit<MeetingMinutes, 'entries'> & { entries: EntryRow[] };

const blankEntry = (): EntryRow => {
  const entryId = newRowId();
  return {
    id: entryId,
    entryId,
    // Actions are what most minute lines turn out to be, and the category drives which
    // fields exist — defaulting here means the common case needs no clicks.
    category: 'action',
    body: '',
    ownerId: '',
    dueDate: '',
    status: 'open',
    requirementIds: [],
    milestoneIds: [],
    initiativeIds: [],
    carriedFromMinutesId: '',
    carriedFromEntryId: '',
    promotedArtefactId: '',
    promotedArtefactType: '',
  };
};

/** A row nobody has typed into. Picking a category alone does not count as content. */
const isBlankEntry = (row: EntryRow): boolean =>
  !entryPlainText(row.body) &&
  !row.ownerId &&
  !row.dueDate &&
  row.requirementIds.length === 0 &&
  row.milestoneIds.length === 0 &&
  !row.carriedFromMinutesId;

const toRow = (entry: MinuteEntry): EntryRow => ({
  ...entry,
  id: entry.entryId || newRowId(),
  requirementIds: entry.requirementIds ?? [],
  milestoneIds: entry.milestoneIds ?? [],
  initiativeIds: entry.initiativeIds ?? [],
});

/** Seed the editable copy, tolerating a record the API returned with fields omitted.
 *  A blank trailing row is materialised here rather than by useRepeatableRows' effect,
 *  so an untouched record never starts life marked dirty. */
const toDraft = (record: MeetingMinutes): MinutesDraft => {
  const entries = (record.entries ?? []).map(toRow);
  return {
    ...record,
    title: record.title ?? '',
    meetingDate: record.meetingDate ?? '',
    startTime: record.startTime ?? '',
    endTime: record.endTime ?? '',
    agenda: record.agenda ?? '',
    initiativeId: record.initiativeId ?? '',
    attendeeIds: record.attendeeIds ?? [],
    apologyIds: record.apologyIds ?? [],
    externalAttendees: record.externalAttendees ?? '',
    entries: entries.length > 0 ? entries : [blankEntry()],
  };
};

/** Back to API shape — the editor's mirror `id` is dropped, and only model attributes
 *  are copied so nothing the editor happens to hang on a row leaks into the payload. */
const toApiEntry = (r: EntryRow): MinuteEntry => ({
  entryId: r.entryId,
  category: r.category,
  body: r.body,
  ownerId: r.ownerId,
  dueDate: r.dueDate,
  status: r.status,
  requirementIds: r.requirementIds,
  milestoneIds: r.milestoneIds,
  initiativeIds: r.initiativeIds,
  carriedFromMinutesId: r.carriedFromMinutesId,
  carriedFromEntryId: r.carriedFromEntryId,
  promotedArtefactId: r.promotedArtefactId,
  promotedArtefactType: r.promotedArtefactType,
});

/** Rows worth persisting. The always-present blank row is dropped. */
const persistableEntries = (rows: EntryRow[]): MinuteEntry[] =>
  rows.filter((r) => !isBlankEntry(r)).map(toApiEntry);

/** Exactly the attributes the API accepts. `status` is deliberately absent — it moves
 *  only through the transition endpoint. */
const writablePayload = (d: MinutesDraft): Partial<MeetingMinutes> => ({
  title: d.title,
  meetingDate: d.meetingDate,
  startTime: d.startTime,
  endTime: d.endTime,
  agenda: d.agenda,
  initiativeId: d.initiativeId,
  attendeeIds: d.attendeeIds,
  externalAttendees: d.externalAttendees,
  apologyIds: d.apologyIds,
  entries: persistableEntries(d.entries),
});

// ─── Errors ───────────────────────────────────────────────────────────────────

/** API failures arrive as an Error whose message may wrap a JSON body. */
const describeApiError = (err: unknown, fallback: string): string => {
  const message = err instanceof Error ? err.message : '';
  const jsonStart = message.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const body = JSON.parse(message.slice(jsonStart)) as { error?: string; validationErrors?: string[] };
      if (body.error) {
        const detail = body.validationErrors?.length ? ` ${body.validationErrors.join(' ')}` : '';
        return `${body.error}.${detail}`;
      }
    } catch {
      /* not a JSON error body — fall through */
    }
  }
  return message || fallback;
};

// ─── Status transitions ───────────────────────────────────────────────────────
// Only the moves that lock the record or withdraw it from review get a confirm.

const TRANSITION_CONFIRM: Partial<Record<MinutesStatus, { title: string; text: string }>> = {
  approved: {
    title: 'Approve these minutes?',
    text: 'Approved is the final status. The minutes become a locked record and cannot be edited or reopened afterwards.',
  },
  draft: {
    title: 'Return these minutes to draft?',
    text: 'They will be withdrawn from review and will need to be circulated again.',
  },
};

const TRANSITION_ICON: Record<MinutesStatus, string> = {
  draft: 'ri-arrow-go-back-line',
  circulated: 'ri-send-plane-line',
  approved: 'ri-checkbox-circle-line',
};

const TRANSITION_BTN_CLASS: Record<MinutesStatus, string> = {
  draft: 'np-btn np-btn-ghost',
  circulated: 'np-btn np-btn-primary',
  approved: 'np-btn np-btn-primary',
};

/** Singular/plural nouns for the header's per-category counts. "1 information" doesn't
 *  read, so the information category counts as notes. */
const CATEGORY_COUNT_NOUN: Record<MinuteCategory, [string, string]> = {
  action: ['action', 'actions'],
  decision: ['decision', 'decisions'],
  risk: ['risk', 'risks'],
  blocker: ['blocker', 'blockers'],
  dependency: ['dependency', 'dependencies'],
  information: ['note', 'notes'],
};

// ─── Small shared chrome ──────────────────────────────────────────────────────

const Req: React.FC = () => (
  <span className="text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

const SectionCard: React.FC<{
  title: string;
  icon: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, actions, children }) => (
  <div className="np-card mb-4">
    <div className="np-card-head">
      <h2>
        <i className={icon}></i> {title}
      </h2>
      {actions}
    </div>
    <div className="np-card-body">{children}</div>
  </div>
);

const Spinner: React.FC = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

// ─── Carry-forward panel ──────────────────────────────────────────────────────

interface CarriedItem {
  source: MeetingMinutes;
  entry: MinuteEntry;
}

type CarriedByCategory = Record<MinuteCategory, CarriedItem[]>;

const emptyCarriedByCategory = (): CarriedByCategory => ({
  action: [],
  decision: [],
  risk: [],
  blocker: [],
  dependency: [],
  information: [],
});

const CarryForwardPanel: React.FC<{
  carriedByCategory: CarriedByCategory;
  isLoading: boolean;
  busyEntryId: string;
  peopleById: Record<string, string>;
  /** Source entryIds that already have a logged-update copy sitting in this meeting. */
  alreadyLoggedEntryIds: Set<string>;
  /** Actions only — closes the source, copies nothing. */
  onMarkDone: (item: CarriedItem) => void;
  /** Actions only — copies here AND closes the source, so it is only ever open in one place. */
  onCarryOver: (item: CarriedItem) => void;
  /** Decision/Risk/Blocker/Dependency/Information — copies here, never touches the source. */
  onLogUpdate: (item: CarriedItem) => void;
  /** Decision/Risk/Blocker/Dependency/Information — closes the source, copies nothing. */
  onResolve: (item: CarriedItem) => void;
}> = ({
  carriedByCategory,
  isLoading,
  busyEntryId,
  peopleById,
  alreadyLoggedEntryIds,
  onMarkDone,
  onCarryOver,
  onLogUpdate,
  onResolve,
}) => {
  const [activeTab, setActiveTab] = useState<MinuteCategory>('action');
  const items = carriedByCategory[activeTab];
  const totalCount = MINUTE_CATEGORIES.reduce((n, c) => n + carriedByCategory[c].length, 0);

  return (
    <SectionCard
      title="Carried forward from previous meetings"
      icon="ri-history-line"
      actions={
        totalCount > 0 ? (
          <span className="np-chip np-tone-warning">
            {totalCount} carried {totalCount === 1 ? 'item' : 'items'}
          </span>
        ) : undefined
      }
    >
      {isLoading ? (
        <p className="np-sub mb-0">Checking earlier minutes for this initiative…</p>
      ) : (
        <>
          <div className="np-tabs mb-3">
            {MINUTE_CATEGORIES.map((c) => {
              const count = carriedByCategory[c].length;
              const selected = activeTab === c;
              return (
                <button
                  key={c}
                  type="button"
                  className={`np-tab d-flex align-items-center gap-1${selected ? ' active' : ''}`}
                  aria-pressed={selected}
                  onClick={() => setActiveTab(c)}
                >
                  <i className={MINUTE_CATEGORY_ICON[c]}></i>
                  {MINUTE_CATEGORY_LABELS[c]}
                  {count > 0 && (
                    <span className={`np-chip np-tone-${MINUTE_CATEGORY_TONE[c]} np-tabular`} style={{ fontSize: '0.65rem' }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {items.length === 0 ? (
            <div className="np-banner np-tone-info">
              <i className="ri-check-double-line"></i>
              <span>
                Nothing carried forward under {MINUTE_CATEGORY_LABELS[activeTab]} from earlier
                meetings on this initiative.
              </span>
            </div>
          ) : (
            <>
              <p className="np-hint mt-0 mb-3">
                {activeTab === 'action'
                  ? 'Close each one where it was raised, or carry it into this meeting — carrying over copies the action here and closes the original, so it is only ever open in one place.'
                  : "Log an update to copy this into today's meeting without touching the original, or mark it resolved where it was raised — nothing here closes just by being carried forward."}
              </p>
              <div className="np-list">
                {items.map(({ source, entry }) => {
                  const takesOwner = categoryTakesOwner(entry.category);
                  const overdue = takesOwner && !!entry.dueDate && entry.dueDate < todayIso();
                  const busy = busyEntryId === entry.entryId;
                  const alreadyLogged = alreadyLoggedEntryIds.has(entry.entryId);
                  return (
                    <div
                      key={`${source.minutesId}-${entry.entryId}`}
                      className="np-list-item"
                      style={{ alignItems: 'flex-start' }}
                    >
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <span className="np-chip np-tone-neutral np-tabular">{source.minutesId}</span>
                          <span className="np-sub">
                            {source.title || 'Untitled meeting'} · {formatDate(source.meetingDate)}
                          </span>
                          {overdue && (
                            <span className="np-chip np-tone-danger">
                              <i className="ri-alarm-warning-line"></i>Overdue — {formatDate(entry.dueDate)}
                            </span>
                          )}
                          {!overdue && takesOwner && entry.dueDate && (
                            <span className="np-chip np-tone-info">Due {formatDate(entry.dueDate)}</span>
                          )}
                          {activeTab !== 'action' && alreadyLogged && (
                            <span className="np-chip np-tone-success" title="A copy of this is already in today's minutes">
                              <i className="ri-check-line"></i>Already logged in this meeting
                            </span>
                          )}
                        </div>
                        <div className="fw-semibold" style={{ fontSize: '0.828rem' }}>
                          {entryPlainText(entry.body) || <span className="np-sub fst-italic">No detail recorded</span>}
                        </div>
                        {takesOwner && (
                          <p className="np-hint">
                            <i className="ri-user-line"></i>{' '}
                            {entry.ownerId ? peopleById[entry.ownerId] ?? entry.ownerId : 'No owner set'}
                          </p>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        {activeTab === 'action' ? (
                          <>
                            <button
                              type="button"
                              className="np-btn np-btn-ghost np-btn-sm"
                              onClick={() => onMarkDone({ source, entry })}
                              disabled={busy}
                            >
                              <i className="ri-check-line"></i>Mark done
                            </button>
                            <button
                              type="button"
                              className="np-btn np-btn-ghost np-btn-sm"
                              onClick={() => onCarryOver({ source, entry })}
                              disabled={busy}
                            >
                              <i className="ri-corner-down-right-line"></i>Carry over
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="np-link-action"
                              onClick={() => onLogUpdate({ source, entry })}
                              disabled={busy || alreadyLogged}
                              title={alreadyLogged ? "Already logged in today's minutes" : undefined}
                            >
                              <i className="ri-corner-down-right-line"></i>
                              {alreadyLogged ? 'Logged' : 'Log update in this meeting'}
                            </button>
                            <button
                              type="button"
                              className="np-link-action"
                              onClick={() => onResolve({ source, entry })}
                              disabled={busy}
                            >
                              <i className="ri-check-line"></i>Mark resolved
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </SectionCard>
  );
};

// ─── Export annex picker ────────────────────────────────────────────────────

/** Categories offered in the export annex. Actions are excluded — a carried action
 *  belongs to this meeting's own Minutes/Actions content already, once carried over,
 *  and an action not yet carried over is not this meeting's to print as an annex. */
const ANNEX_CATEGORIES: MinuteCategory[] = MINUTE_CATEGORIES.filter((c) => c !== 'action');

const AnnexPickerModal: React.FC<{
  carriedByCategory: CarriedByCategory;
  onSkip: () => void;
  onExport: (categories: MinuteCategory[]) => void;
}> = ({ carriedByCategory, onSkip, onExport }) => {
  const [selected, setSelected] = useState<Set<MinuteCategory>>(
    () => new Set(ANNEX_CATEGORIES.filter((c) => carriedByCategory[c].length > 0))
  );

  const toggle = (c: MinuteCategory) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  return (
    <NimbusModal size="lg" onDialogClick={(e) => e.stopPropagation()}>
      <NimbusModalContent
        title="Include carried-forward items as an annex?"
        subtitle="These were carried forward from earlier meetings on this initiative. Pick what belongs at the end of this PDF."
        onClose={onSkip}
        footer={
          <>
            <button type="button" className="np-btn np-btn-ghost" onClick={onSkip}>
              Skip annex
            </button>
            <button type="button" className="np-btn np-btn-primary" onClick={() => onExport([...selected])}>
              <i className="ri-file-pdf-2-line"></i>Export PDF
            </button>
          </>
        }
      >
        <div className="np-list">
          {ANNEX_CATEGORIES.map((c) => {
            const count = carriedByCategory[c].length;
            return (
              <label
                key={c}
                className="np-list-item"
                style={{ cursor: count > 0 ? 'pointer' : 'default', opacity: count > 0 ? 1 : 0.6 }}
              >
                <input
                  type="checkbox"
                  className="form-check-input flex-shrink-0"
                  checked={selected.has(c)}
                  disabled={count === 0}
                  onChange={() => toggle(c)}
                />
                <span className={`np-chip np-tone-${MINUTE_CATEGORY_TONE[c]}`}>
                  <i className={MINUTE_CATEGORY_ICON[c]}></i>
                  {MINUTE_CATEGORY_LABELS[c]}
                </span>
                <span className="np-sub flex-grow-1">
                  {count > 0 ? `${count} carried forward` : 'Nothing carried forward'}
                </span>
              </label>
            );
          })}
        </div>
      </NimbusModalContent>
    </NimbusModal>
  );
};

// ─── Editor ───────────────────────────────────────────────────────────────────

const MinutesEditor: React.FC<{ record: MeetingMinutes }> = ({ record }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const [draft, setDraft] = useState<MinutesDraft>(() => toDraft(record));
  const [dirty, setDirty] = useState(false);

  const readOnly = draft.status === 'approved';

  const update = useCallback((patch: Partial<Omit<MinutesDraft, 'entries'>>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const setEntries = useCallback((entries: EntryRow[]) => {
    setDraft((prev) => ({ ...prev, entries }));
    setDirty(true);
  }, []);

  const { rows, add, canAdd, patch, remove, canRemove } = useRepeatableRows<EntryRow>({
    stored: draft.entries,
    makeBlank: blankEntry,
    isBlank: isBlankEntry,
    onChange: setEntries,
  });

  // ── Reference data ────────────────────────────────────────────────────────
  const { data: people = [] } = useQuery<Person[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const peopleOptions = useMemo<SelectOption[]>(
    () => people.map((p) => ({ value: p.personId, label: p.personName })),
    [people]
  );
  const peopleById = useMemo<Record<string, string>>(
    () => Object.fromEntries(people.map((p) => [p.personId, p.personName])),
    [people]
  );

  const { data: initiatives = [] } = useQuery<Initiative[]>({
    queryKey: ['initiatives'],
    queryFn: initiativesApi.getAll,
  });
  const initiativeOptions = useMemo<SelectOption[]>(
    () => initiatives.map((i) => ({ value: i.initiativeId, label: i.name })),
    [initiatives]
  );

  // The list endpoint is not guaranteed to carry the full plan, so the pinned initiative
  // is fetched on its own for the requirement/milestone option lists.
  const { data: initiative } = useQuery<Initiative>({
    queryKey: ['initiative', draft.initiativeId],
    queryFn: () => initiativesApi.getOne(draft.initiativeId),
    enabled: !!draft.initiativeId,
  });

  const requirementOptions = useMemo<SelectOption[]>(
    () =>
      [
        ...(initiative?.functionalRequirements ?? []),
        ...(initiative?.nonFunctionalRequirements ?? []),
      ].map((r) => ({ value: r.id, label: r.text })),
    [initiative]
  );
  const milestoneOptions = useMemo<SelectOption[]>(
    () => (initiative?.milestones ?? []).map((m) => ({ value: m.id, label: m.name })),
    [initiative]
  );

  // ── Persistence ───────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (payload: Partial<MeetingMinutes>) => meetingMinutesApi.update(record.minutesId, payload),
    onSuccess: (saved) => {
      queryClient.setQueryData(['meetingMinutes', record.minutesId], saved);
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
    },
  });

  /** Validate, then write. Returns false so callers can abandon whatever they were
   *  about to do rather than moving on from unsaved work. */
  const persist = async (): Promise<boolean> => {
    if (readOnly) return true;
    if (!draft.title.trim()) {
      toast.error('Give the meeting a title before saving.');
      return false;
    }
    if (!draft.meetingDate) {
      toast.error('Set the meeting date before saving.');
      return false;
    }
    try {
      await saveMutation.mutateAsync(writablePayload(draft));
      setDirty(false);
      toast.success('Minutes updated.');
      return true;
    } catch (err) {
      toast.error(describeApiError(err, 'Could not save these minutes.'));
      return false;
    }
  };

  const transitionMutation = useMutation({
    mutationFn: (status: MinutesStatus) => meetingMinutesApi.transition(record.minutesId, status),
    onSuccess: (updated, status) => {
      queryClient.setQueryData(['meetingMinutes', record.minutesId], updated);
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      // Merged over the current draft rather than replacing it: the transition endpoint
      // is free to answer with just the moved record's status fields.
      setDraft((prev) => toDraft({ ...prev, ...updated }));
      setDirty(false);
      toast.success(`Minutes moved to ${MINUTES_STATUS_LABELS[status]}.`);
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not change the status of these minutes.')),
  });

  const handleTransition = async (status: MinutesStatus) => {
    // Circulating or approving publishes whatever is on screen — save it first so the
    // snapshot the reviewers see is the one the author is looking at.
    if (dirty && !(await persist())) return;
    const confirmCopy = TRANSITION_CONFIRM[status];
    if (confirmCopy && !(await swal.confirm(confirmCopy.title, confirmCopy.text))) return;
    transitionMutation.mutate(status);
  };

  const deleteMutation = useMutation({
    mutationFn: () => meetingMinutesApi.delete(record.minutesId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      toast.success(`${record.minutesId} deleted.`);
      navigate('/meeting-minutes', { replace: true });
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not delete these minutes.')),
  });

  const handleDelete = async () => {
    if (!(await swal.confirmDelete(draft.title || record.minutesId))) return;
    deleteMutation.mutate();
  };

  // archivedAt is a plain writable field (not a status transition - see WRITABLE_ATTRIBUTES'
  // own comment), so this goes through the generic update endpoint rather than /transition.
  // Deliberately available regardless of status/readOnly - archiving an approved record is
  // the whole point, not an edge case to guard against.
  const archiveMutation = useMutation({
    mutationFn: (archivedAt: string) => meetingMinutesApi.update(record.minutesId, { archivedAt }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['meetingMinutes', record.minutesId], updated);
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      setDraft((prev) => toDraft({ ...prev, ...updated }));
      toast.success(updated.archivedAt ? 'Minutes archived.' : 'Minutes unarchived.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not change the archived state of these minutes.')),
  });

  const handleToggleArchive = () => {
    archiveMutation.mutate(draft.archivedAt ? '' : new Date().toISOString());
  };

  // ── Carry-forward ─────────────────────────────────────────────────────────
  const carryEnabled = !!draft.initiativeId && !readOnly;

  const { data: initiativeMinutes = [], isLoading: carryLoading } = useQuery<MeetingMinutes[]>({
    queryKey: ['meetingMinutes', 'initiative', draft.initiativeId],
    queryFn: () => meetingMinutesApi.list(draft.initiativeId),
    enabled: carryEnabled,
  });

  // Grouped by category rather than filtered to actions: every category can be carried
  // forward, but only actions are ever *consumed* by it (see the module comment above).
  // `status !== 'done'` rather than `=== 'open'` reads the same for today's two-value enum
  // and says directly what the panel means by "carried forward" — still outstanding.
  const carriedByCategory = useMemo<CarriedByCategory>(() => {
    const byCategory = emptyCarriedByCategory();
    [...initiativeMinutes]
      .filter((m) => m.minutesId !== record.minutesId)
      .sort((a, b) => (b.meetingDate || '').localeCompare(a.meetingDate || ''))
      .forEach((m) => {
        (m.entries ?? []).forEach((entry) => {
          if (entry.status !== 'done') byCategory[entry.category].push({ source: m, entry });
        });
      });
    return byCategory;
  }, [initiativeMinutes, record.minutesId]);

  // Which source entries already have a "logged update" copy sitting in *this* meeting's
  // own entries, keyed by the source entry's own id (carriedFromEntryId — set only by
  // logUpdateMutation/carryOverMutation below, never by hand). Lets the panel show
  // "already logged in this meeting" instead of leaving a just-acted-on item looking
  // untouched, without changing the non-consuming carry-forward semantics themselves.
  const alreadyLoggedEntryIds = useMemo<Set<string>>(
    () => new Set(draft.entries.map((e) => e.carriedFromEntryId).filter(Boolean)),
    [draft.entries]
  );

  /** Resolve an entry on the record that raised it — never on a copy. Goes through the
   *  dedicated close-action endpoint rather than a full `update`, because the source
   *  meeting may already be approved (nothing about approval requires its open items to
   *  be resolved first) — a plain PUT would 409 on a locked record and leave the item
   *  stuck open forever. Works for any category (lambda_function.py's close_action). */
  const closeOnSource = (source: MeetingMinutes, entryId: string) =>
    meetingMinutesApi.closeAction(source.minutesId, entryId);

  /** "Mark done" (actions) and "Mark resolved" (every other category) are the same
   *  operation under different labels — both just close the source and copy nothing. */
  const resolveMutation = useMutation({
    mutationFn: ({ source, entry }: CarriedItem) => closeOnSource(source, entry.entryId),
    onSuccess: (_data, { entry }) => {
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      toast.success(`${MINUTE_CATEGORY_LABELS[entry.category]} resolved on the minutes that raised it.`);
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not resolve that item.')),
  });

  /** Actions only — copies the entry here and closes the source in the same operation,
   *  so an action is only ever open in one place. */
  const carryOverMutation = useMutation({
    mutationFn: async ({ source, entry }: CarriedItem) => {
      const copy: MinuteEntry = {
        ...entry,
        entryId: newRowId(),
        status: 'open',
        carriedFromMinutesId: source.minutesId,
        carriedFromEntryId: entry.entryId,
        // The copy has not been escalated — only the original ever was.
        promotedArtefactId: '',
        promotedArtefactType: '',
      };
      const nextEntries = [...persistableEntries(draft.entries), copy];
      // This meeting is written first: if the source update then fails the action is
      // open in two places, which is recoverable. The reverse would lose it entirely.
      const saved = await meetingMinutesApi.update(record.minutesId, {
        ...writablePayload(draft),
        entries: nextEntries,
      });
      await closeOnSource(source, entry.entryId);
      return { saved, nextEntries };
    },
    onSuccess: ({ saved, nextEntries }) => {
      queryClient.setQueryData(['meetingMinutes', record.minutesId], saved);
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      // Rebuilt from what was sent rather than from the response, so the panel is correct
      // whatever shape the API answers with. Row ids track entryIds, so only the new row
      // mounts — the editors already on screen are left alone.
      setDraft((prev) => ({ ...prev, entries: nextEntries.map(toRow) }));
      setDirty(false);
      toast.success('Action carried over, and closed on the minutes that raised it.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not carry that action over.')),
  });

  /** Decision/Risk/Blocker/Dependency/Information only — copies the entry here and never
   *  touches the source, since these categories are a live reference, not a task that
   *  moves. The non-consuming counterpart to carryOverMutation above. */
  const logUpdateMutation = useMutation({
    mutationFn: async ({ source, entry }: CarriedItem) => {
      const copy: MinuteEntry = {
        ...entry,
        entryId: newRowId(),
        status: 'open',
        carriedFromMinutesId: source.minutesId,
        carriedFromEntryId: entry.entryId,
        promotedArtefactId: '',
        promotedArtefactType: '',
      };
      const nextEntries = [...persistableEntries(draft.entries), copy];
      const saved = await meetingMinutesApi.update(record.minutesId, {
        ...writablePayload(draft),
        entries: nextEntries,
      });
      return { saved, nextEntries };
    },
    onSuccess: ({ saved, nextEntries }) => {
      queryClient.setQueryData(['meetingMinutes', record.minutesId], saved);
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      setDraft((prev) => ({ ...prev, entries: nextEntries.map(toRow) }));
      setDirty(false);
      toast.success('Logged in this meeting — the original is unchanged.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not log that update in this meeting.')),
  });

  const handleCarryOver = (item: CarriedItem) => {
    // Carrying over writes this whole record, so it has to be valid first.
    if (!draft.title.trim() || !draft.meetingDate) {
      toast.error('Give this meeting a title and a date before carrying actions into it.');
      return;
    }
    carryOverMutation.mutate(item);
  };

  const handleLogUpdate = (item: CarriedItem) => {
    if (!draft.title.trim() || !draft.meetingDate) {
      toast.error('Give this meeting a title and a date before logging updates into it.');
      return;
    }
    logUpdateMutation.mutate(item);
  };

  const busyEntryId =
    (resolveMutation.isPending && resolveMutation.variables?.entry.entryId) ||
    (carryOverMutation.isPending && carryOverMutation.variables?.entry.entryId) ||
    (logUpdateMutation.isPending && logUpdateMutation.variables?.entry.entryId) ||
    '';

  // ── PDF export ────────────────────────────────────────────────────────────
  // Prints what is on screen, unsaved edits included: generating a PDF changes no server
  // state, and "give me what I am looking at" is the whole point of the control. The blank
  // trailing entry row is dropped the same way a save drops it, and person / initiative ids
  // are resolved through the reference data this page already holds.
  const [showAnnexPicker, setShowAnnexPicker] = useState(false);

  const runExport = (annexCategories: MinuteCategory[] = []) => {
    try {
      // Kept grouped by category (not flattened) so the PDF prints one heading per category,
      // matching how the main body's own entries are grouped.
      const annex: Partial<Record<MinuteCategory, CarriedItem[]>> = {};
      annexCategories.forEach((c) => {
        if (carriedByCategory[c].length) annex[c] = carriedByCategory[c];
      });
      exportMeetingMinutesPdf(
        { ...draft, entries: persistableEntries(draft.entries) },
        people,
        initiatives,
        Object.keys(annex).length ? { annex } : undefined
      );
      toast.success(
        dirty ? 'PDF exported — it includes the unsaved changes on screen.' : 'PDF exported.'
      );
    } catch (err) {
      toast.error(`Could not generate the PDF: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setShowAnnexPicker(false);
    }
  };

  const hasCarriedAnnexItems = ANNEX_CATEGORIES.some((c) => carriedByCategory[c].length > 0);

  const handleExportPdf = () => {
    // Nothing to ask about for a standalone meeting, or one with nothing carried forward
    // outside of actions — export immediately exactly as this control always has.
    if (!draft.initiativeId || !hasCarriedAnnexItems) {
      runExport();
      return;
    }
    setShowAnnexPicker(true);
  };

  // ── Escalation ────────────────────────────────────────────────────────────
  // The risk register is the system of record for risks; a minute entry only seeds one.
  // The entry text is handed over as ?title= — the parent agent wires the back-link.
  const handlePromote = async (entry: MinuteEntry) => {
    if (dirty && !(await persist())) return;
    const seed = entryPlainText(entry.body).slice(0, 200);
    navigate(`/risks/new?title=${encodeURIComponent(seed)}`);
  };

  // ── Unsaved-changes guard ─────────────────────────────────────────────────
  // The app mounts a BrowserRouter, not a data router, so react-router's useBlocker is
  // unavailable and in-app navigation cannot be intercepted. This covers closing the tab
  // or reloading; the "Unsaved changes" chip in the header covers the rest.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Derived display ───────────────────────────────────────────────────────
  const transitions = MINUTES_STATUS_TRANSITIONS[draft.status];
  const realEntries = useMemo(() => rows.filter((r) => !isBlankEntry(r)), [rows]);
  const categoryCounts = useMemo(() => {
    const counts: Partial<Record<MinuteEntry['category'], number>> = {};
    realEntries.forEach((r) => {
      counts[r.category] = (counts[r.category] ?? 0) + 1;
    });
    return counts;
  }, [realEntries]);
  const openActions = realEntries.filter((r) => r.category === 'action' && r.status === 'open').length;

  // The Minutes card's own category tabs default to 'all' — live note-taking during a
  // meeting jumps between categories as the conversation does, and forcing a tab click
  // per topic switch would fight that. A specific tab is a browsing/editing filter on
  // top of the same list, not a separate document.
  const [activeEntryTab, setActiveEntryTab] = useState<'all' | MinuteCategory>('all');
  const visibleRows = activeEntryTab === 'all' ? rows : rows.filter((r) => r.category === activeEntryTab);
  const visibleRealEntries =
    activeEntryTab === 'all' ? realEntries : realEntries.filter((r) => r.category === activeEntryTab);

  const selectedInitiative = draft.initiativeId
    ? initiativeOptions.find((o) => o.value === draft.initiativeId) ?? {
        value: draft.initiativeId,
        label: initiative?.name ?? draft.initiativeId,
      }
    : null;

  const selectedPeople = (ids: string[]): SelectOption[] =>
    ids.map((id) => peopleOptions.find((o) => o.value === id) ?? { value: id, label: peopleById[id] ?? id });

  const handleInitiativeChange = (initiativeId: string) => {
    const hadLinks = draft.entries.some(
      (e) => e.requirementIds.length > 0 || e.milestoneIds.length > 0
    );
    setDraft((prev) => ({
      ...prev,
      initiativeId,
      // Requirement and milestone ids belong to the initiative they came from — keeping
      // them across a re-pin would leave entries linked to another initiative's plan.
      entries: hadLinks
        ? prev.entries.map((e) => ({ ...e, requirementIds: [], milestoneIds: [] }))
        : prev.entries,
    }));
    setDirty(true);
    if (hadLinks) toast.info('Requirement and milestone links were cleared — they belonged to the previous initiative.');
  };

  const saving = saveMutation.isPending;
  const busy =
    saving ||
    transitionMutation.isPending ||
    deleteMutation.isPending ||
    carryOverMutation.isPending ||
    logUpdateMutation.isPending ||
    archiveMutation.isPending;

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-file-list-3-line"
        colour={MINUTES_STATUS_TONE[draft.status]}
        title={draft.title || 'Untitled meeting'}
        subtitle={
          draft.meetingDate
            ? `${formatDate(draft.meetingDate)}${draft.startTime ? ` · ${draft.startTime}${draft.endTime ? `–${draft.endTime}` : ''}` : ''}`
            : 'No meeting date set'
        }
        breadcrumbs={[{ label: 'Meeting Minutes', to: '/meeting-minutes' }, { label: record.minutesId }]}
        actions={
          <>
            <span className={`np-chip np-tone-${MINUTES_STATUS_TONE[draft.status]}`}>
              {MINUTES_STATUS_LABELS[draft.status]}
            </span>

            {/* Deliberately available in every status, approved included — a locked,
                approved set of minutes is exactly what gets circulated as a PDF. */}
            <button
              className="np-btn np-btn-ghost"
              onClick={handleExportPdf}
              title="Download these minutes as a PDF"
            >
              <i className="ri-file-pdf-2-line"></i>Export PDF
            </button>

            {!readOnly && (
              <button className="np-btn np-btn-primary" onClick={() => void persist()} disabled={busy}>
                <i className="ri-save-line"></i>
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}

            {/* Approved is terminal — MINUTES_STATUS_TRANSITIONS.approved is empty. */}
            {transitions.map((s) => (
              <button
                key={s}
                className={TRANSITION_BTN_CLASS[s]}
                onClick={() => void handleTransition(s)}
                disabled={busy}
              >
                <i className={TRANSITION_ICON[s]}></i>
                {MINUTES_ACTION_LABELS[s]}
              </button>
            ))}

            {/* Independent of status/readOnly on purpose - archiving an approved record is
                the normal case, not something to guard against. */}
            <button
              className="np-btn np-btn-ghost"
              onClick={handleToggleArchive}
              disabled={busy}
              title={draft.archivedAt ? 'Bring these minutes back into the everyday view' : 'Hide these minutes from the everyday view'}
            >
              <i className={draft.archivedAt ? 'ri-inbox-unarchive-line' : 'ri-archive-line'}></i>
              {draft.archivedAt ? 'Unarchive' : 'Archive'}
            </button>

            {/* Approved minutes are the governance record — deleting one from here would
                defeat the lock, so the control stays visible but refuses. */}
            <button
              className="np-btn np-btn-ghost-danger"
              onClick={() => void handleDelete()}
              disabled={busy || readOnly}
              title={readOnly ? 'Approved minutes are a locked record and cannot be deleted' : 'Delete these minutes'}
            >
              <i className="ri-delete-bin-line"></i>Delete
            </button>

            <button className="np-btn np-btn-ghost" onClick={() => navigate('/meeting-minutes')}>
              <i className="ri-arrow-left-line"></i>Back
            </button>
          </>
        }
      />

      <div className="d-flex align-items-center gap-2 flex-wrap mb-4">
        <span className="np-chip np-tone-neutral np-tabular">{record.minutesId}</span>
        {MINUTE_CATEGORIES.filter((c) => categoryCounts[c]).map((c) => {
          const n = categoryCounts[c] ?? 0;
          return (
            <span key={c} className={`np-chip np-tone-${MINUTE_CATEGORY_TONE[c]}`}>
              <i className={MINUTE_CATEGORY_ICON[c]}></i>
              {n} {CATEGORY_COUNT_NOUN[c][n === 1 ? 0 : 1]}
            </span>
          );
        })}
        {openActions > 0 && (
          <span className="np-chip np-tone-warning">
            <i className="ri-time-line"></i>
            {openActions} still open
          </span>
        )}
        {dirty && (
          <span className="np-chip np-tone-warning">
            <i className="ri-edit-circle-line"></i>Unsaved changes
          </span>
        )}
      </div>

      {readOnly && (
        <div className="np-banner np-tone-success mb-4" role="status">
          <i className="ri-lock-line"></i>
          <span>
            <strong>These minutes are approved.</strong> Approved is the final status, so the record
            is locked — nothing here can be edited, and no further actions can be carried into it.
            {draft.approvedBy && ` Approved by ${peopleById[draft.approvedBy] ?? draft.approvedBy}`}
            {draft.approvedAt && ` on ${formatDate(draft.approvedAt)}`}
            {draft.approvedBy || draft.approvedAt ? '.' : ''}
          </span>
        </div>
      )}

      {/* Independent of the approved-lock banner above - a record can be both, since
          archiving never touches status. */}
      {draft.archivedAt && (
        <div className="np-banner np-tone-neutral mb-4" role="status">
          <i className="ri-archive-line"></i>
          <span>
            <strong>These minutes are archived.</strong> Hidden from the everyday list view as of{' '}
            {formatDate(draft.archivedAt)} — unarchive to bring it back, or it's still reachable directly
            by this link.
          </span>
        </div>
      )}

      {/* ── The meeting ─────────────────────────────────────────────────────── */}
      <SectionCard title="The meeting" icon="ri-calendar-event-line">
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="np-field mb-0">
              <label htmlFor="mm-title">
                Title
                <Req />
              </label>
              <input
                id="mm-title"
                type="text"
                className="np-input"
                placeholder="e.g. Platform migration — weekly steering"
                value={draft.title}
                disabled={readOnly}
                required
                onChange={(e) => update({ title: e.target.value })}
              />
            </div>
          </div>

          <div className="col-12 col-sm-4 col-lg-2">
            <div className="np-field mb-0">
              <label htmlFor="mm-date">
                Date
                <Req />
              </label>
              <input
                id="mm-date"
                type="date"
                className="np-input"
                value={draft.meetingDate}
                disabled={readOnly}
                required
                onChange={(e) => update({ meetingDate: e.target.value })}
              />
            </div>
          </div>

          <div className="col-6 col-sm-4 col-lg-2">
            <div className="np-field mb-0">
              <label htmlFor="mm-start">Start</label>
              <input
                id="mm-start"
                type="time"
                className="np-input"
                value={draft.startTime}
                disabled={readOnly}
                onChange={(e) => update({ startTime: e.target.value })}
              />
            </div>
          </div>

          <div className="col-6 col-sm-4 col-lg-2">
            <div className="np-field mb-0">
              <label htmlFor="mm-end">End</label>
              <input
                id="mm-end"
                type="time"
                className="np-input"
                value={draft.endTime}
                disabled={readOnly}
                onChange={(e) => update({ endTime: e.target.value })}
              />
            </div>
          </div>

          <div className="col-12">
            <div className="np-field mb-0">
              <label htmlFor="mm-initiative">Initiative</label>
              <Select
                inputId="mm-initiative"
                options={initiativeOptions}
                value={selectedInitiative}
                onChange={(opt) => handleInitiativeChange(opt ? opt.value : '')}
                placeholder="Not pinned to an initiative"
                isDisabled={readOnly}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                styles={reactSelectStyles(readOnly)}
              />
              <p className="np-hint">
                Pinning the meeting to an initiative is what enables carry-forward of open actions
                and linking entries to requirements and milestones. Leave it clear for a standalone
                meeting.
              </p>
            </div>
          </div>

          <div className="col-12">
            <div className="np-field mb-0">
              {/* No htmlFor: the control is a Quill editor, not a labellable form element. */}
              <label>Agenda</label>
              {readOnly ? (
                <RichTextView value={draft.agenda} empty="No agenda was recorded." />
              ) : (
                <RichTextField
                  value={draft.agenda}
                  onChange={(agenda) => update({ agenda })}
                  placeholder="Optional — what the meeting set out to cover."
                  height={120}
                />
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Who was there ───────────────────────────────────────────────────── */}
      <SectionCard title="Attendance" icon="ri-team-line">
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="np-field mb-0">
              <label htmlFor="mm-attendees">Attendees</label>
              <Select
                inputId="mm-attendees"
                options={peopleOptions}
                value={selectedPeople(draft.attendeeIds)}
                onChange={(opts) => update({ attendeeIds: opts.map((o) => o.value) })}
                placeholder="Who was in the room?"
                isDisabled={readOnly}
                isMulti
                isSearchable
                menuPortalTarget={document.body}
                styles={reactSelectStyles(readOnly)}
              />
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="np-field mb-0">
              <label htmlFor="mm-apologies">Apologies</label>
              <Select
                inputId="mm-apologies"
                options={peopleOptions}
                value={selectedPeople(draft.apologyIds)}
                onChange={(opts) => update({ apologyIds: opts.map((o) => o.value) })}
                placeholder="Who was invited but could not attend?"
                isDisabled={readOnly}
                isMulti
                isSearchable
                menuPortalTarget={document.body}
                styles={reactSelectStyles(readOnly)}
              />
            </div>
          </div>

          <div className="col-12">
            <div className="np-field mb-0">
              <label htmlFor="mm-external">External attendees</label>
              <input
                id="mm-external"
                type="text"
                className="np-input"
                placeholder="e.g. Jo Bakke (Contoso), Sam Reid (auditor)"
                value={draft.externalAttendees}
                disabled={readOnly}
                onChange={(e) => update({ externalAttendees: e.target.value })}
              />
              <p className="np-hint">
                Anyone who is not a Nimbus person record — suppliers, auditors, candidates.
              </p>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Carry-forward ───────────────────────────────────────────────────── */}
      {carryEnabled && (
        <CarryForwardPanel
          carriedByCategory={carriedByCategory}
          isLoading={carryLoading}
          busyEntryId={busyEntryId}
          peopleById={peopleById}
          alreadyLoggedEntryIds={alreadyLoggedEntryIds}
          onMarkDone={(item) => resolveMutation.mutate(item)}
          onCarryOver={handleCarryOver}
          onLogUpdate={handleLogUpdate}
          onResolve={(item) => resolveMutation.mutate(item)}
        />
      )}

      {showAnnexPicker && (
        <AnnexPickerModal
          carriedByCategory={carriedByCategory}
          onSkip={() => runExport()}
          onExport={(categories) => runExport(categories)}
        />
      )}

      {/* ── Entries ─────────────────────────────────────────────────────────── */}
      <SectionCard
        title="Minutes"
        icon="ri-file-list-3-line"
        actions={
          <span className="np-chip np-tone-neutral">
            {realEntries.length} {realEntries.length === 1 ? 'entry' : 'entries'}
          </span>
        }
      >
        <div className="np-tabs mb-3">
          <button
            type="button"
            className={`np-tab d-flex align-items-center gap-1${activeEntryTab === 'all' ? ' active' : ''}`}
            aria-pressed={activeEntryTab === 'all'}
            onClick={() => setActiveEntryTab('all')}
          >
            <i className="ri-list-check"></i>All
            {realEntries.length > 0 && (
              <span className="np-chip np-tone-neutral np-tabular" style={{ fontSize: '0.65rem' }}>
                {realEntries.length}
              </span>
            )}
          </button>
          {MINUTE_CATEGORIES.map((c) => {
            const count = categoryCounts[c] ?? 0;
            const selected = activeEntryTab === c;
            return (
              <button
                key={c}
                type="button"
                className={`np-tab d-flex align-items-center gap-1${selected ? ' active' : ''}`}
                aria-pressed={selected}
                onClick={() => setActiveEntryTab(c)}
              >
                <i className={MINUTE_CATEGORY_ICON[c]}></i>
                {MINUTE_CATEGORY_LABELS[c]}
                {count > 0 && (
                  <span className={`np-chip np-tone-${MINUTE_CATEGORY_TONE[c]} np-tabular`} style={{ fontSize: '0.65rem' }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {readOnly && visibleRealEntries.length === 0 && (
          <div className="text-center py-5">
            <i className="ri-file-list-3-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
            <p className="np-sub mb-0">
              {activeEntryTab === 'all'
                ? 'No entries were recorded in this meeting.'
                : `No ${MINUTE_CATEGORY_LABELS[activeEntryTab].toLowerCase()} entries were recorded in this meeting.`}
            </p>
          </div>
        )}

        {!readOnly && activeEntryTab !== 'all' && visibleRows.length === 0 && (
          <p className="np-hint mt-0 mb-3">
            No {MINUTE_CATEGORY_LABELS[activeEntryTab].toLowerCase()} entries yet in this meeting.
          </p>
        )}

        {(readOnly ? visibleRealEntries : visibleRows).map((row, i) => (
          <MinuteEntryRow
            key={row.id}
            entry={row}
            index={i}
            onChange={(changes) => patch(row.id, changes)}
            onRemove={() => remove(row.id)}
            canRemove={canRemove}
            readOnly={readOnly}
            people={peopleOptions}
            requirements={requirementOptions}
            milestones={milestoneOptions}
            initiatives={initiativeOptions}
            onPromote={(entry) => void handlePromote(entry)}
          />
        ))}

        {!readOnly && (
          <div className="d-flex align-items-center gap-2 mt-3">
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => add(activeEntryTab === 'all' ? undefined : { category: activeEntryTab })}
              disabled={activeEntryTab === 'all' && !canAdd}
              title={
                activeEntryTab !== 'all' || canAdd
                  ? `Add ${activeEntryTab === 'all' ? 'another entry' : MINUTE_CATEGORY_LABELS[activeEntryTab].toLowerCase()}`
                  : 'Write the entry above first'
              }
            >
              <i className="ri-add-line"></i>
              Add {activeEntryTab === 'all' ? 'entry' : MINUTE_CATEGORY_LABELS[activeEntryTab].toLowerCase()}
            </button>
            {activeEntryTab === 'all' && !canAdd && (
              <span className="np-hint m-0">Write the entry above to add another.</span>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

// ─── Route shell ──────────────────────────────────────────────────────────────

export const MeetingMinutesDetail: React.FC = () => {
  const { minutesId } = useParams<{ minutesId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { personId } = useCurrentUser();

  // /meeting-minutes/new is its own route, so the param is absent there; the explicit
  // 'new' check also covers the route being wired as a single :minutesId pattern.
  const isNew = !minutesId || minutesId === 'new';
  const createStarted = useRef(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['meetingMinutes', minutesId],
    queryFn: () => meetingMinutesApi.get(minutesId!),
    enabled: !isNew && !!minutesId,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      meetingMinutesApi.create({
        title: '',
        meetingDate: todayIso(),
        startTime: '',
        endTime: '',
        agenda: '',
        initiativeId: '',
        // Whoever opens the form was at the meeting often enough for this to save a click,
        // and it is trivially removable if they were only taking the notes.
        attendeeIds: personId ? [personId] : [],
        apologyIds: [],
        externalAttendees: '',
        entries: [],
      }),
    onSuccess: (created) => {
      queryClient.setQueryData(['meetingMinutes', created.minutesId], created);
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      navigate(`/meeting-minutes/${created.minutesId}`, { replace: true });
    },
    onError: (err) => {
      toast.error(describeApiError(err, 'Could not start a new set of minutes.'));
      navigate('/meeting-minutes', { replace: true });
    },
  });

  useEffect(() => {
    if (!isNew || createStarted.current) return;
    createStarted.current = true; // StrictMode double-invokes effects — never mint two records
    createMutation.mutate();
    // createMutation is rebuilt each render; the ref above is the real guard.
  }, [isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isNew || isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <PageHeader
          icon="ri-file-list-3-line"
          title="Minutes not found"
          subtitle={`No meeting minutes could be loaded for ${minutesId ?? 'this reference'}.`}
          breadcrumbs={[{ label: 'Meeting Minutes', to: '/meeting-minutes' }, { label: 'Not found' }]}
          actions={
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/meeting-minutes')}>
              <i className="ri-arrow-left-line"></i>Back to register
            </button>
          }
        />
        <div className="np-banner np-tone-danger" role="alert">
          <i className="ri-error-warning-line"></i>
          These minutes may have been deleted, or you may not have access to them.
        </div>
      </div>
    );
  }

  // Keyed on the record so switching records rebuilds the draft from scratch, while a
  // background refetch of the same record never clobbers work in progress.
  return <MinutesEditor key={data.minutesId} record={data} />;
};

export default MeetingMinutesDetail;
